/**
 * @fileoverview Hook to resolve human-readable labels for statistics dimensions from metadata.
 * @module shared/hooks/useLabelResolver
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDashboardData } from './dashboard/useDashboardData'

/**
 * A map of dimension -> (key -> label)
 */
export type LabelMap = Record<string, Record<string, string>>

/**
 * System variant raw labels that need translation.
 * Maps the backend label to the i18n key suffix.
 */
const SYSTEM_VARIANT_KEYS: Record<string, string> = {
  __unmapped__: 'unassigned',
  Unassigned: 'unassigned',
  General: 'storeWide',
  'Store-Wide': 'storeWide',
}

/**
 * Hook that merges label maps from available statistics queries and exposes
 * helpers to resolve labels for dimension keys.
 *
 * Sources:
 * - salesTimeQuery.metadata.labels
 * - salesProductQuery.metadata.labels
 *
 * If no label exists, it gracefully falls back to the provided key.
 *
 * @returns Helpers for resolving labels and the merged label map
 * @example
 * ```ts
 * const { labelFor, productLabel } = useLabelResolver()
 * const name = productLabel('ab-pro-two') // => 'AbyssBlock Pro 2 …' or 'ab-pro-two'
 * ```
 */
export function useLabelResolver(): {
  labels: LabelMap
  labelFor: (dimension: string, key: string | null | undefined) => string
  productLabel: (key: string | null | undefined) => string
  countryLabel: (code: string | null | undefined) => string
} {
  const { t } = useTranslation('common')
  const { salesTimeQuery, salesProductQuery, salesProductOnlyQuery } = useDashboardData()

  const labels = useMemo<LabelMap>(() => {
    const map: LabelMap = {}

    const merge = (src?: LabelMap): void => {
      if (!src) return
      for (const dim of Object.keys(src)) {
        map[dim] = { ...(map[dim] || {}), ...src[dim] }
      }
    }

    merge(salesTimeQuery.data?.metadata?.labels)
    merge(salesProductQuery.data?.metadata?.labels)
    merge(salesProductOnlyQuery.data?.metadata?.labels)

    return map
  }, [salesTimeQuery.data, salesProductQuery.data, salesProductOnlyQuery.data])

  const labelFor = (dimension: string, key: string | null | undefined): string => {
    if (!key) return ''
    const byDim = labels[dimension]
    if (!byDim) return String(key)
    const rawLabel = byDim[key] ?? String(key)
    // Transform system variant labels to translated user-friendly names
    const systemKey = SYSTEM_VARIANT_KEYS[rawLabel]
    if (systemKey) {
      return t(`systemVariants.${systemKey}`)
    }
    return rawLabel
  }

  const productLabel = (key: string | null | undefined): string => labelFor('product', key)
  const countryLabel = (code: string | null | undefined): string => labelFor('country', code)

  return { labels, labelFor, productLabel, countryLabel }
}
